import makeStyles from '@mui/styles/makeStyles';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import {AKAIcon, AlertIcon, Bear} from '../icons/icons';
import React, {useEffect, useState} from 'react';
import {StyledBadge} from '../header/patientInfo';
import {useTranslation} from 'react-i18next';
import Popover from '@mui/material/Popover';
import clsx from 'clsx';
import _ from 'lodash';
import moment from 'moment';
import {getConfig} from '../../util/configUtil';
import {getSafeguardingLevel, isPaedsPatient, selectSafeLevelColor} from '../../util/patientContextUtil';
import {getPatientFlagValue} from '../../pages/ward/mainScreen/util/patientFlagsUtil';
import {AgyleNamedIcon} from '../icons/agyle';
import {useHeaderIconStyles} from './components/iconStyle';

const useStyles = makeStyles((theme) =>
    ({
        cpis: {
            display: 'flex',
            width: 35,
            height: 25,
            padding: 10,
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.palette.attendanceType.ed,
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 'bold',
            userSelect: 'none',
        },
        divRow: {
            display: 'flex',
            flexWrap: 'nowrap',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 5,
        },
        popoverMenu: {
            padding: 10,
            backgroundColor: theme.palette.inPatient.inputBackground,
            fontSize: '0.75rem',
            fontWeight: 500,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
        },
        alertBoldText: {
            fontWeight: 'bold',
        },
    }),
);

const PatientHeaderMainIcon = ({patient, fromSearch = false}) => {
    const classes = useStyles();
    const classesIcons = useHeaderIconStyles();
    const {t} = useTranslation();

    const patientObject = patient?.patient;

    // Configs
    const defaultDateFormat = getConfig('defaultDateFormat');
    const safeguardingLevelMandatoryConfig = getConfig('safeguardingLevelMandatory', false);

    // Show popup
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [popoverType, setPopoverType] = React.useState(null);
    const open = Boolean(anchorEl);

    // Icon state settings
    const [safeguardingLevel, setSafeguardingLevel] = useState(null);
    const [deceased, setDeceased] = useState(false);

    // Check safeguarding level for children patients and deceased flag for all type patients
    useEffect(() => {
        if (patient?.metadata?.patientFlags) {
            setDeceased(getPatientFlagValue(patient, 'deceased'));
        }

        if (patient && isPaedsPatient(patient.patient.dob)) {
            setSafeguardingLevel(getSafeguardingLevel(patient));
        }
    }, [patient]);

    const handleClickMenu = (e, type) => {
        setPopoverType(type);
        setAnchorEl(e.currentTarget);
    };

    const getNumberOf = (type) => {
        if (!patientObject) return null;
        return patientObject[type] == null ? false : patientObject[type].length;
    };

    const handleClose = (e) => {
        setAnchorEl(null);
    };

    const getPatientAka = () => {
        let aka = [];

        if (patientObject?.name) {
            _.forEach(patientObject.names, (akaName, index) => {
                let firstName = '';
                if (akaName.firstName) {
                    firstName = akaName.firstName + ' ';
                }
                let middleNames = '';
                if (akaName.middleNames) {
                    middleNames = akaName.middleNames + ' ';
                }
                let familyName = ' ';
                if (akaName.familyName) {
                    familyName = akaName.familyName + ' ';
                }
                let nameType = '(missing)';
                if (akaName.nameType) {
                    nameType = `(${akaName.nameType})`;
                }
                aka.push(firstName + middleNames + familyName + nameType);
            });
        }

        return aka.map((aka, index) => <div key={index}>{aka}</div>);
    };

    const getPatientAlert = () => {
        let arr = [];

        if (patientObject?.alerts) {
            _.forEach(patientObject.alerts, (alert, index) => {
                if (!alert.hideInUI) {
                    let endDateStr = '';
                    if (alert.endDate != null) {
                        endDateStr = ' Until' + ': '
                            + moment.utc(alert.endDate).format(defaultDateFormat);
                    }
                    arr.push(
                        <div key={index} className="grid">
                            <div>
                                {alert.alertType == null ? '' : alert.alertType}
                                <div>
                                    {alert.group == null ? '' : alert.group}
                                </div>
                            </div>

                            <div>
                                <div className={classes.alertBoldText}>
                                    {alert.allergen == null ? '' : alert.allergen}
                                </div>

                                <div>
                                    {alert.description == null ? '' : alert.description}
                                </div>

                                <div>
                                    {alert.comments == null ? '' : alert.comments}
                                </div>

                                {alert.startDate !== null && (
                                    <div className={classes.alertBoldText}>
                                        {`${t('Since')}: ${moment.utc(alert.startDate).format(defaultDateFormat)}`}
                                    </div>
                                )}

                                <div className={classes.alertBoldText}>
                                    {endDateStr}
                                </div>
                            </div>
                        </div>,
                    );
                }
            });
        }

        return arr;
    };

    const getContent = () => {
        switch (popoverType) {
            case 'aka':
                return getPatientAka();
            case 'alert':
                return getPatientAlert();
            default:
                return null;
        }
    };

    const numbOfAlerts = getNumberOf('alerts');
    const numbOfAka = getNumberOf('names');

    return (
        <>
            <div className={classes.divRow}>
                {(fromSearch || numbOfAlerts) &&
                    <Tooltip title={t('Patient alerts')}>
                        <span>
                           <IconButton
                               onClick={(e) => handleClickMenu(e, 'alert')}
                               size="small"
                               disableRipple
                               disabled={!numbOfAlerts}
                               color={(anchorEl && popoverType === 'alert') ? 'primary' : 'default'}
                               className={clsx(fromSearch && classesIcons.iconHover)}
                           >
                            <StyledBadge
                                badgeContent={numbOfAlerts || 0}
                                color="primary"
                            >
                                <AlertIcon/>
                                </StyledBadge>
                            </IconButton>
                        </span>
                    </Tooltip>
                }

                {(fromSearch || numbOfAka) &&
                    <Tooltip title={t('Patient A.K.A')}>
                        <span>
                            <IconButton
                                onClick={(e) => handleClickMenu(e, 'aka')}
                                size="small"
                                disableRipple
                                disabled={!numbOfAka}
                                color={(anchorEl && popoverType === 'aka') ? 'primary' : 'default'}
                                className={clsx(fromSearch && classesIcons.iconHover)}

                            >
                                <AKAIcon/>
                            </IconButton>
                        </span>
                    </Tooltip>
                }

                {!fromSearch && patient?.admission?.cpisFlag === 'Y' &&
                    <Tooltip title={t('Child Protection - Information Sharing')}>
                        <div className={classes.cpis}>
                            {t('CPIS')}
                        </div>
                    </Tooltip>
                }


                {!fromSearch && (safeguardingLevel && safeguardingLevelMandatoryConfig) &&
                    <Tooltip title={t('Paediatric Safeguarding Level')}>
                        <span>
                            <IconButton
                                disabled
                                style={{color: selectSafeLevelColor(safeguardingLevel)}}
                                size="small"
                                disableRipple
                            >
                                <Bear/>
                            </IconButton>
                        </span>
                    </Tooltip>
                }

                {(deceased && !fromSearch) &&
                    <Tooltip title={t('Patient Deceased')}>
                        <span>
                            <IconButton
                                size="small"
                                disableRipple
                            >
                                <AgyleNamedIcon name="deceased"/>
                            </IconButton>
                        </span>
                    </Tooltip>
                }
            </div>

            <Popover
                classes={{paper: clsx('scroll-normal-white', classes.popoverMenu)}}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                {getContent()}
            </Popover>
        </>
    );
};

export default PatientHeaderMainIcon;